import React, { useState, useEffect } from 'react';
import ReactPlayer from 'react-player';
import './VideoRenderer.css';


const VideoRenderer = ({ url }) => {
  const [error, setError] = useState(null);
  const [isValidUrl, setIsValidUrl] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Function to validate and clean video URLs
  const validateAndCleanUrl = (url) => {
    if (!url || typeof url !== 'string') return null;
    
    let cleanUrl = url.trim();
    
    // Add https:// if the URL doesn't have a protocol
    if (!cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://')) {
      cleanUrl = `https://${cleanUrl}`;
    }
    
    // Check if it's a valid URL format
    try {
      new URL(cleanUrl);
    } catch {
      // If it's still not a valid URL, it might be a video ID
      // Try to construct proper URLs for common platforms
      
      // Reset to original for ID checking
      const originalUrl = url.trim();
      
      // YouTube video ID pattern
      if (/^[a-zA-Z0-9_-]{11}$/.test(originalUrl)) {
        return `https://www.youtube.com/watch?v=${originalUrl}`;
      }
      
      // Vimeo video ID pattern
      if (/^\d{8,}$/.test(originalUrl)) {
        return `https://vimeo.com/${originalUrl}`;
      }
      
      return null;
    }
    
    // For YouTube URLs, ensure they're in the correct format
    if (cleanUrl.includes('youtube.com') || cleanUrl.includes('youtu.be')) {
      // Extract video ID from various YouTube URL formats
      const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
      const match = cleanUrl.match(youtubeRegex);
      
      if (match && match[1]) {
        return `https://www.youtube.com/watch?v=${match[1]}`;
      }
    }
    
    // For Vimeo URLs
    if (cleanUrl.includes('vimeo.com')) {
      const vimeoRegex = /(?:vimeo\.com\/)(\d+)/;
      const match = cleanUrl.match(vimeoRegex);
      
      if (match && match[1]) {
        return `https://vimeo.com/${match[1]}`;
      }
    }
    
    return cleanUrl;
  };

  useEffect(() => {
    setIsLoading(true);
    setError(null);
    
    if (!url) {
      setError('No video URL provided');
      setIsValidUrl(false);
      setIsLoading(false);
      return;
    }

    const cleanedUrl = validateAndCleanUrl(url);
    
    if (!cleanedUrl) {
      setError('Invalid video URL format');
      setIsValidUrl(false);
      setIsLoading(false);
      return;
    }

    // Check if ReactPlayer can handle this URL
    if (!ReactPlayer.canPlay(cleanedUrl)) {
      setError('Unsupported video format or platform');
      setIsValidUrl(false);
      setIsLoading(false);
      return;
    }

    setIsValidUrl(true);
    setIsLoading(false);
  }, [url]);

  const handleError = (error) => {
    console.error('Video playback error:', error);
    setError('Failed to load video. Please check the URL and try again.');
  };

  const handleReady = () => {
    setError(null);
  };

  if (isLoading) {
    return (
      <div className="videoContainer">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading video...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="videoContainer">
        <div className="error">
          <div className="error-icon">⚠️</div>
          <p>{error}</p>
          {url && (
            <div className="error-details">
              <p>URL: {url}</p>
              <button 
                onClick={() => window.location.reload()} 
                className="retry-button"
              >
                Retry
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!isValidUrl) {
    return (
      <div className="videoContainer">
        <div className="error">
          <div className="error-icon">❌</div>
          <p>Invalid video URL</p>
        </div>
      </div>
    );
  }

  const cleanedUrl = validateAndCleanUrl(url);

  return (
    <div className="videoContainer">
      <ReactPlayer
        url={cleanedUrl}
        controls
        width="100%"
        height="100%"
        playsinline
        config={{
          youtube: { 
            playerVars: { 
              modestbranding: 1, 
              rel: 0, 
              showinfo: 0,
              origin: window.location.origin // Helps with CORS issues
            } 
          },
          vimeo: {
            playerOptions: {
              responsive: true
            }
          },
          file: {
            attributes: {
              controlsList: 'nodownload',
              onContextMenu: e => e.preventDefault(),
              crossOrigin: 'anonymous'
            }
          }
        }}
        onError={handleError}
        onReady={handleReady}
        onLoadStart={() => setIsLoading(true)}
        onLoadedData={() => setIsLoading(false)}
        fallback={
          <div className="loading">
            <div className="spinner"></div>
            <p>Loading video...</p>
          </div>
        }
      />
    </div>
  );
};

export default VideoRenderer;
